// src/components/VerovioScoreViewer.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { DEFAULT_BPM } from "../constants";

const VerovioScoreViewer = ({
  svgs,
  timemap,
  currentTime,
  onNoteClick,
  allNotes = [],
  bpmScalingFactor,
  onAskStudyvarious,
  measureTimes = [],
}) => {
  const containerRef = useRef(null);
  const [activeNoteIds, setActiveNoteIds] = useState(new Set());
  const highlightRectRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, measureNumber: null });

  const handleNoteClick = useCallback((event) => {
    if (!onNoteClick || !timemap) return;
    
    const noteId = event.currentTarget.id;
    if (!noteId) return;
    
    // Find the event where this note starts
    const timemapEvent = timemap.find(ev => ev.on && ev.on.includes(noteId));
    if (!timemapEvent) return;
    
    // Convert tstamp (ms) to piece time
    const verovioTime = timemapEvent.tstamp / 1000 / bpmScalingFactor / 2; // seconds
    const pieceTime = verovioTime;
    
    onNoteClick(pieceTime);
  }, [onNoteClick, timemap]);

  const handleNoteHover = useCallback((event) => {
    if (!timemap || !allNotes.length) return;
    
    const noteId = event.currentTarget.id;
    if (!noteId) return;
    
    // Find the event where this note starts
    const timemapEvent = timemap.find(ev => ev.on && ev.on.includes(noteId));
    if (!timemapEvent) return;
    
    // Convert tstamp to piece time
    const verovioTime = timemapEvent.tstamp / 1000 / bpmScalingFactor;
    
    const pieceTime = (verovioTime);
    
    // Find the corresponding note in allNotes (closest by time)
    const noteTime = pieceTime; // pieceTime is already in the correct scale
    const closestNote = allNotes.reduce((closest, note) => {
      const currentNoteTime = note.time; // scale note.time to match piece time
      const diff = Math.abs(currentNoteTime - noteTime);
      const closestDiff = closest ? Math.abs(closest.time - noteTime) : Infinity;
      return diff < closestDiff ? note : closest;
    }, null);
    
    if (closestNote && closestNote.name) {
      const rect = event.currentTarget.getBoundingClientRect();
      
      setTooltip({
        visible: true,
        text: closestNote.name,
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
    }
  }, [timemap, allNotes]);

  const handleNoteLeave = useCallback(() => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
  }, []);

  const handleNoteContextMenu = useCallback((event) => {
    event.preventDefault();
    if (!onAskStudyvarious) return;

    const noteId = event.currentTarget.id;
    if (!noteId) return;

    // Find the event where this note starts
    const timemapEvent = timemap.find(ev => ev.on && ev.on.includes(noteId));
    if (!timemapEvent) return;

    // Convert tstamp to piece time (similar to handleNoteClick)
    const verovioTime = timemapEvent.tstamp / 1000 / bpmScalingFactor; // seconds
    const pieceTime = verovioTime;

    // Find the measure that contains this time
    let measureNumber = null;
    for (let i = 0; i < measureTimes.length; i++) {
      const measure = measureTimes[i];
      const nextMeasure = measureTimes[i + 1];
      const measureStart = measure.start_time * bpmScalingFactor / 2;
      const measureEnd = nextMeasure ? nextMeasure.start_time * bpmScalingFactor / 2 : Infinity;
      // round to 2 digits after decimal
      const roundedPieceTime = Math.round(pieceTime * 100) / 100;
      const roundedMeasureStart = Math.round(measureStart * 100) / 100;
      const roundedMeasureEnd = Math.round(measureEnd * 100) / 100;
      
      if (roundedPieceTime >= roundedMeasureStart && roundedPieceTime < roundedMeasureEnd) {
        measureNumber = measure.measure_number;
        // console.log('Found measure number:', measureNumber, 'for pieceTime:', pieceTime, 'between', measureStart, 'and', measureEnd);
        // console.log('Rounded values:', roundedPieceTime, roundedMeasureStart, roundedMeasureEnd);
        break;
      }
    }

    if (measureNumber) {
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        measureNumber,
      });
    }
  }, [onAskStudyvarious, timemap, bpmScalingFactor, measureTimes]);

  const handleContextMenuClick = useCallback(() => {
    if (contextMenu.measureNumber && onAskStudyvarious) {
      onAskStudyvarious(contextMenu.measureNumber);
    }
    setContextMenu({ visible: false, x: 0, y: 0, measureNumber: null });
  }, [contextMenu.measureNumber, onAskStudyvarious]);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, measureNumber: null });
  }, []);

  /* --------------------------------------------------------------
   * 1. Compute active note IDs
   * ------------------------------------------------------------ */
  useEffect(() => {
    if (!timemap || timemap.length === 0) return;

    const scaledMs = currentTime * 1000 / bpmScalingFactor;
    let active = [];

    for (let i = timemap.length - 1; i >= 0; i--) {
      const ev = timemap[i];
      if (ev.tstamp <= scaledMs) {
        const on = ev.on || [];
        const off = ev.off || [];
        active = on.filter(id => !off.includes(id));
        break;
      }
    }

    setActiveNoteIds(new Set(active));
  }, [currentTime, timemap]);


  /* --------------------------------------------------------------
   * 2. Highlight notes + measure
   * ------------------------------------------------------------ */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear note highlights
    container.querySelectorAll(".playing").forEach(el => el.classList.remove("playing"));

    // Apply note highlight
    activeNoteIds.forEach(id => {
      const el = container.querySelector(`#${CSS.escape(id)}`);
      if (el) el.classList.add("playing");
    });

    // Remove old measure highlight
    if (highlightRectRef.current) {
      highlightRectRef.current.remove();
      highlightRectRef.current = null;
    }

    // Find first active note element
    let noteEl = null;
    for (const id of activeNoteIds) {
      const el = container.querySelector(`#${CSS.escape(id)}`);
      if (el) { noteEl = el; break; }
    }
    if (!noteEl) return;

    // Find owning <g class="measure">
    let measureEl = noteEl;
    while (measureEl && !measureEl.classList?.contains("measure")) {
      measureEl = measureEl.parentElement;
    }
    if (!measureEl) return;

    // Get measure bounding box
    const bbox = measureEl.getBBox();
    const svg = measureEl.ownerSVGElement;
    if (!svg) return;

    // Create highlight rectangle
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("class", "measure-highlight");
    rect.setAttribute("x", bbox.x);
    rect.setAttribute("y", bbox.y);
    rect.setAttribute("width", bbox.width);
    rect.setAttribute("height", bbox.height);
    rect.setAttribute("fill", "rgba(230, 230, 230, 0.45)");
    rect.setAttribute("rx", "6");
    rect.style.pointerEvents = "none";

    // Insert rectangle at bottom of measure group
    measureEl.insertBefore(rect, measureEl.firstChild);
    highlightRectRef.current = rect;

    const measureBox = measureEl.getBoundingClientRect();
    const containerBox = container.getBoundingClientRect();

    const margin = 400;

    if (measureBox.top < containerBox.top + margin ||
        measureBox.bottom > containerBox.bottom - margin) {

      const scrollTarget =
        container.scrollTop +
        (measureBox.top - containerBox.top) -
        containerBox.height / 2 +
        measureBox.height / 2;

      container.scrollTo({
        top: scrollTarget,
        behavior: "smooth",
      });
    }
  }, [activeNoteIds]);


  /* --------------------------------------------------------------
   * 4. Attach click and hover handlers to notes
   * ------------------------------------------------------------ */
  useEffect(() => {
    if (!onNoteClick && !allNotes.length && !onAskStudyvarious) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Find all note elements (assuming they have class 'note' and an id)
    const noteElements = container.querySelectorAll('g.note[id]');
    
    noteElements.forEach(el => {
      el.style.cursor = 'pointer';
      if (onNoteClick) {
        el.addEventListener('click', handleNoteClick);
      }
      if (allNotes.length) {
        el.addEventListener('mouseover', handleNoteHover);
        el.addEventListener('mouseout', handleNoteLeave);
      }
      if (onAskStudyvarious) {
        el.addEventListener('contextmenu', handleNoteContextMenu);
      }
    });
    
    // Cleanup
    return () => {
      noteElements.forEach(el => {
        if (onNoteClick) {
          el.removeEventListener('click', handleNoteClick);
        }
        if (allNotes.length) {
          el.removeEventListener('mouseover', handleNoteHover);
          el.removeEventListener('mouseout', handleNoteLeave);
        }
        if (onAskStudyvarious) {
          el.removeEventListener('contextmenu', handleNoteContextMenu);
        }
      });
    };
  }, [svgs, timemap, onNoteClick, handleNoteClick, allNotes, handleNoteHover, handleNoteLeave, onAskStudyvarious, handleNoteContextMenu, measureTimes]);


  /* --------------------------------------------------------------
   * 5. Close context menu on outside click
   * ------------------------------------------------------------ */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        const target = event.target;
        if (!target.closest('.context-menu')) {
          handleContextMenuClose();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenu.visible, handleContextMenuClose]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        const target = event.target;
        if (!target.closest('.context-menu')) {
          handleContextMenuClose();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [contextMenu.visible, handleContextMenuClose]);


  /* --------------------------------------------------------------
   * Render
   * ------------------------------------------------------------ */
  if (!svgs || svgs.length === 0) {
    return <div style={{ padding: "2rem", color: "#888" }}>Loading score…</div>;
  }

  return (
    <div
      ref={containerRef}
      className="verovio-score-container"
      style={{
        height: "100vh",
        overflowY: "auto",
        background: "white",
        padding: "1rem",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      {svgs.map((svgString, i) => (
        <div
          key={i}
          dangerouslySetInnerHTML={{ __html: svgString }}
          style={{
            marginBottom: "3rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderRadius: "8px",
            background: "white",
            overflowX: "auto",
            maxWidth: "100%",
          }}
        />
      ))}

      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            pointerEvents: 'none',
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 1000,
            whiteSpace: 'nowrap'
          }}
        >
          {tooltip.text}
        </div>
      )}

      {contextMenu.visible && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1001,
          }}
          onClick={handleContextMenuClick}
        >
          <div className="context-menu-item">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px', opacity: 0.7}}>
              <path d="M14 17h3l2-4V7h-6v6h3M6 17h3l2-4V7H5v6h3l-2 4z" fill="currentColor"/>
            </svg>
            Ask Studyvarious
          </div>
        </div>
      )}

      <style>{`
        .verovio-score-container .playing {
          fill: #e63946 !important;
          stroke: #e63946 !important;
          stroke-width: 2 !important;
        }
        .verovio-score-container g.note:not(.playing) {
          opacity: 1.;
        }
        .verovio-score-container svg {
          max-width: 100%;
          height: auto;
        }
        .measure-highlight {
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default VerovioScoreViewer;
